// src/components/About.js
import React from 'react';
import { Container } from '@mui/material';
import { Link } from 'react-router-dom';

const steps = [
  {
    num: '01',
    icon: '🏁',
    title: 'Submit Your Laps',
    body: 'Visit any participating track, set your best lap time, and submit it with video proof. Every approved lap earns championship points.',
  },
  {
    num: '02',
    icon: '📈',
    title: 'Circuit Value Grows',
    body: 'The more drivers that compete at a track, the more championship points that circuit becomes worth. Popular tracks become high-stakes battles.',
  },
  {
    num: '03',
    icon: '🏆',
    title: 'Win Global Glory',
    body: 'Be the fastest at your local tracks and climb the worldwide leaderboard. The top drivers earn recognition and exclusive prizes.',
  },
];

function About() {
  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pb: 8 }}>

        {/* ── HERO ── */}
        <div style={{
          position: 'relative',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          marginBottom: 48,
          background: 'var(--text-primary)',
          minHeight: 300,
          display: 'flex',
          alignItems: 'flex-end',
          padding: '40px 48px',
        }}>
          {/* Track image bg */}
          <img
            src="/model3-track-action.jpg"
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%', opacity: 0.35 }}
          />
          {/* Gradient overlay */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 100%)' }} />
          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 640 }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(25,130,206,0.9)',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '4px 12px',
              borderRadius: 'var(--radius-pill)',
              marginBottom: 16,
            }}>
              Global EV Time Attack Series
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.4rem, 5vw, 3.6rem)',
              color: '#fff',
              lineHeight: 1,
              letterSpacing: '0.03em',
              marginBottom: 16,
              fontWeight: 400,
            }}>
              Model 3<br />Challenge
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', lineHeight: 1.6, maxWidth: 500 }}>
              The world's first global electric vehicle time attack championship — compete at your local track, earn points worldwide.
            </p>
          </div>
        </div>

        {/* ── TWO-COLUMN CONCEPT ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 48 }}>
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '28px 32px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', color: 'var(--accent)', lineHeight: 1, marginBottom: 12 }}>GLOBAL</div>
            <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Compete Without Travelling</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7 }}>
              Challenge enthusiasts worldwide without leaving your local area. The Model 3 Challenge brings professional time attack competition to your doorstep through a revolutionary points-based championship system.
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7, marginTop: 12 }}>
              Every lap you complete at your local track contributes to both local bragging rights and global championship standings.
            </p>
          </div>
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '28px 32px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.4rem', color: 'var(--accent)', lineHeight: 1, marginBottom: 12 }}>LOCAL</div>
            <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 700, marginBottom: 12 }}>Build Your Community</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7 }}>
              Gather your local Model 3 enthusiasts and turn track days into championship events. The more drivers that participate at a circuit, the more championship points that circuit is worth.
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7, marginTop: 12 }}>
              Create lasting friendships, push each other to improve, and represent your local scene in the global battle for supremacy.
            </p>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={{ marginBottom: 48 }}>
          <h2 className="section-heading" style={{ marginTop: 0, textAlign: 'center', marginBottom: 28 }}>How It Works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {steps.map((step) => (
              <div key={step.num} style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: '28px 24px',
                boxShadow: 'var(--shadow-sm)',
                position: 'relative',
                overflow: 'hidden',
              }}>
                {/* Big number watermark */}
                <div style={{
                  position: 'absolute',
                  top: -8,
                  right: 12,
                  fontFamily: 'var(--font-display)',
                  fontSize: '5rem',
                  color: 'var(--accent-subtle)',
                  lineHeight: 1,
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}>
                  {step.num}
                </div>
                <div style={{ fontSize: '2rem', marginBottom: 14 }}>{step.icon}</div>
                <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1rem', marginBottom: 10 }}>{step.title}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.7 }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── PRIZE BANNER ── */}
        <div style={{
          background: 'var(--text-primary)',
          borderRadius: 'var(--radius-xl)',
          padding: '40px 48px',
          marginBottom: 48,
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          flexWrap: 'wrap',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Subtle pattern */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(25,130,206,0.15) 0%, transparent 60%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(240,165,0,0.15)',
              color: 'var(--gold)',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '4px 12px',
              borderRadius: 'var(--radius-pill)',
              marginBottom: 14,
            }}>
              🏆 Championship Prize
            </div>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
              color: '#fff',
              lineHeight: 1.1,
              letterSpacing: '0.02em',
              marginBottom: 12,
              fontWeight: 400,
            }}>
              Exclusive Prize from<br />Mountain Pass Performance
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 480 }}>
              The season champion receives exclusive performance parts and accessories from Mountain Pass Performance — the premier destination for Tesla Model 3 modifications and upgrades.
            </p>
          </div>
          <a
            href="https://www.MountainPassPerformance.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'relative',
              display: 'inline-block',
              background: 'var(--accent)',
              color: '#fff',
              padding: '12px 24px',
              borderRadius: 'var(--radius-md)',
              fontWeight: 700,
              fontSize: '0.875rem',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'background var(--transition)',
            }}
          >
            Visit Mountain Pass →
          </a>
        </div>

        {/* ── CTA ── */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '40px 48px',
          textAlign: 'center',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <h2 className="section-heading" style={{ marginTop: 0, marginBottom: 12, textAlign: 'center' }}>Ready to Join the Challenge?</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 600, margin: '0 auto 28px' }}>
            Whether you're a seasoned track enthusiast or just getting started with your Model 3, the Model 3 Challenge welcomes drivers of all skill levels.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/signup" style={{
              background: 'var(--accent)',
              color: '#fff',
              padding: '12px 28px',
              borderRadius: 'var(--radius-md)',
              fontWeight: 700,
              fontSize: '0.9rem',
              textDecoration: 'none',
              display: 'inline-block',
              transition: 'background var(--transition)',
            }}>
              Create Your Profile
            </Link>
            <Link to="/" style={{
              background: 'transparent',
              color: 'var(--accent)',
              padding: '12px 28px',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: '0.9rem',
              textDecoration: 'none',
              display: 'inline-block',
              border: '1px solid var(--accent)',
            }}>
              View Leaderboard
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '32px 0 0', borderTop: '1px solid var(--border)', marginTop: 32 }}>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>
            Model 3 Challenge — Bringing electric vehicle performance to the masses
          </p>
        </div>
      </Container>
    </div>
  );
}

export default About;
